import { useState, useEffect } from "react";
import { useFirstVisit } from "../useFirstVisit";

export type SplashState = "loading" | "ready" | "error" | "offline-error";

interface VisitorSplashOptions {
  orgSlug: string;
  isOffline: boolean;
  isFromCache: boolean;
  isError: boolean;
  hasMapData: boolean;
  isFetching: boolean;
  hasIntent: boolean;
}

export function useVisitorSplash({
  orgSlug,
  isOffline,
  isFromCache,
  isError,
  hasMapData,
  isFetching,
  hasIntent,
}: VisitorSplashOptions) {
  const [splashMinTimePassed, setSplashMinTimePassed] = useState(false);
  const [splashDismissed, setSplashDismissed] = useState(false);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);

  const { isFirstVisit, markVisited } = useFirstVisit(orgSlug);

  // Splash min time timer (shortened when from cache)
  const splashMinTimeMs = isFromCache ? 300 : 500;
  useEffect(() => {
    const timer = setTimeout(() => {
      setSplashMinTimePassed(true);
    }, splashMinTimeMs);
    return () => clearTimeout(timer);
  }, [splashMinTimeMs]);

  // Determine splash state
  const splashState: SplashState = (() => {
    if (isOffline && !hasMapData) return "offline-error";
    if (isError) return "error";
    if (hasMapData && splashMinTimePassed) return "ready";
    return "loading";
  })();

  // Auto-dismiss splash when ready
  useEffect(() => {
    if (splashState === "ready" && splashMinTimePassed && !splashDismissed) {
      setSplashDismissed(true);
    }
  }, [splashState, splashMinTimePassed, splashDismissed]);

  // Show welcome modal after splash dismisses (first visit, no deep link intent)
  useEffect(() => {
    if (splashDismissed && isFirstVisit && !hasIntent && hasMapData) {
      setWelcomeModalOpen(true);
    }
  }, [splashDismissed, isFirstVisit, hasIntent, hasMapData]);

  // Mark map ready when data is loaded
  useEffect(() => {
    if (hasMapData && !isFetching) {
      setIsMapReady(true);
    }
  }, [hasMapData, isFetching]);

  // Show splash for loading, error, or offline-error states
  const showSplash =
    !splashDismissed &&
    (splashState === "loading" ||
      splashState === "error" ||
      splashState === "offline-error" ||
      (splashState === "ready" && !splashMinTimePassed));

  const dismissSplash = () => setSplashDismissed(true);

  const handleWelcomeDismiss = () => {
    markVisited();
    setWelcomeModalOpen(false);
  };

  return {
    splashState,
    showSplash,
    welcomeModalOpen,
    isMapReady,
    dismissSplash,
    handleWelcomeDismiss,
  };
}
